import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtomValue } from "jotai";
import { Button } from "wiz-front-shared/components/button/Button";
import { OTPInput, OTPInputRef } from "components/common/input/OTPInput";
import { InputInfoMsg } from "components/common/input/Input";
import { userStore } from "store/userStore";
import config from "config/config";
import "./Identity.css";

export type AuthType = "OTP" | "DANAL_PARENT" | "DANAL_SELF";

interface DanalParams {
    name: string;
    phoneNumber: string;
    birth: string;          // YYMMDD (이미 6자리)
    genderDigit: string;    // "1"~"4"
    carrier: string;        // SKT/KTF/...
    /** 다날 서버에 보호자 인증 여부 전달 (DANAL_PARENT 일 때만 true) */
    parentConsent: boolean;
}

interface SmsParams {
    phoneNumber: string;
    countryCode: string;       // "+82" 등
    verifyPurpose: string;     // "SIGNUP" / "FIND_ACCOUNT" / "PARENT_VERIFY" 등
    templateCode?: string;     // 기본 "SMS_VERIFICATION"
    codeLength?: number;       // 기본 6
    expireMinutes?: number;    // 기본 3
    memberType?: string;       // 기본 "USER"
}

export interface IdentityOtpResult {
    authType: AuthType;
    verifyId?: number;
    identityVerificationId?: string;
}

interface Props {
    serviceId: string;
    authType: AuthType;
    /** 다날 인증 정보 (DANAL_* 일 때 필수) */
    danal?: DanalParams;
    /** SMS 인증 정보 (OTP 일 때 필수) */
    sms?: SmsParams;
    /** 인증 성공 콜백 */
    onSuccess: (result: IdentityOtpResult) => void;
}

/**
 * 본인인증 - OTP 입력 / 발송 / 검증
 *
 * - DANAL_*: 포트원(다날) API
 * - OTP: 일반 SMS OTP (해외)
 * - SignUp / Recover 양쪽에서 공유
 */
/**
 * 수신자 언어에 맞는 SMS 템플릿 코드를 만든다.
 *
 * wiz-message 의 tb_sms_template 은 (service_id, template_code) 가 유니크라 언어 컬럼이 없다.
 * 스키마를 바꾸지 않고 코드에 접미사를 붙여 문안을 구분한다.
 *   SMS_VERIFICATION      한국어(기본)
 *   SMS_VERIFICATION_EN   영어 — 한국어 외 전 언어
 *
 * [언어별로 나누지 않고 영문으로 통일한 이유]
 * 인증 문자는 코드 전달이 전부라 번역의 실익이 작은 반면, 언어를 늘릴수록
 * 템플릿 조합이 늘고 누락 시 발송이 실패한다. 또한 영문은 GSM-7 로 인코딩되어
 * 세그먼트당 160자인데 일본어·한국어는 UCS-2 로 70자여서, 영문이 도달률과 비용에 유리하다.
 *
 * 한국어를 남긴 것은 한국 미성년자가 보호자 다날 인증 실패 후 이 경로로 넘어오기 때문이다.
 * (SignUpStep5: danalRound >= 1 이면 OTP)
 */
const ENGLISH_TEMPLATE_SUFFIX = "_EN";

const toLocalizedTemplateCode = (baseCode: string, language: string): string => {
    // i18n 언어는 "ko", "ko-KR" 등으로 올 수 있어 앞부분만 본다.
    const lang = (language || "").split("-")[0];
    return lang === "ko" || lang === "" ? baseCode : `${baseCode}${ENGLISH_TEMPLATE_SUFFIX}`;
};

export const IdentityOtpForm = (props: Props) => {
    const { t, i18n } = useTranslation();

    const { serviceId, authType, danal, sms, onSuccess } = props;
    const isDanal = authType === "DANAL_PARENT" || authType === "DANAL_SELF";

    const [otp, setOtp] = useState("");
    const otpRef = useRef<OTPInputRef>(null);
    const [otpInfoMsg, setOtpInfoMsg] = useState<InputInfoMsg | undefined>();
    const [isBtnDisabled, setIsBtnDisabled] = useState(true);

    const [isSending, setIsSending] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [verifyId, setVerifyId] = useState<number | null>(null);
    const [identityVerificationId, setIdentityVerificationId] = useState<string | null>(null);
    const [isInitialSent, setIsInitialSent] = useState(false);

    const sendVerifyCodeState = useAtomValue(userStore.sendVerifyCode.stateAtom);
    const checkVerifyCodeState = useAtomValue(userStore.checkVerifyCode.stateAtom);
    const portOneSendState = useAtomValue(userStore.portOneSend.stateAtom);
    const portOneConfirmState = useAtomValue(userStore.portOneConfirm.stateAtom);

    // 진입 시 자동 포커스 + 자동 발송
    useEffect(() => {
        const timer = setTimeout(() => otpRef.current?.focus(), 300);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (!isInitialSent) {
            setOtp("");
            setVerifyId(null);
            setIdentityVerificationId(null);
            setOtpInfoMsg(undefined);
            setIsBtnDisabled(true);
            otpRef.current?.resetTimer();
            sendAuthRequest();
            setIsInitialSent(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sendAuthRequest = () => {
        setIsSending(true);
        if (isDanal && danal) {
            userStore.onPortOneSend({
                serviceId,
                name: danal.name,
                phoneNumber: danal.phoneNumber,
                birth: danal.birth,
                genderDigit: danal.genderDigit,
                carrier: danal.carrier,
                parentConsent: danal.parentConsent,
            });
        } else if (sms) {
            userStore.onSendVerifyCode({
                serviceId,
                countryCode: sms.countryCode,
                phoneNumber: sms.phoneNumber,
                verifyPurpose: sms.verifyPurpose,
                templateCode: toLocalizedTemplateCode(
                    sms.templateCode || "SMS_VERIFICATION",
                    i18n.language,
                ),
                codeLength: sms.codeLength ?? 6,
                expireMinutes: sms.expireMinutes ?? 3,
                memberType: sms.memberType || "USER",
            });
        }
    };

    // SMS 발송 응답
    useEffect(() => {
        if (sendVerifyCodeState.status === "ready" && isSending) {
            setIsSending(false);
            const data = sendVerifyCodeState.data?.data;
            if (data?.status === "SENT" && data?.verifyId) {
                setVerifyId(data.verifyId);
                setTimeout(() => otpRef.current?.startCountDown(true), 100);
            } else {
                setOtpInfoMsg({ msg: t("signUp.smsErrorMsg"), type: "error" });
            }
            userStore.sendVerifyCode.onDefault();
        } else if (sendVerifyCodeState.status === "error" && isSending) {
            setIsSending(false);
            userStore.sendVerifyCode.onDefault();
            const errorCode = sendVerifyCodeState.error?.code;
            setOtpInfoMsg({
                msg: t(errorCode === "INTERNAL_ERROR" ? "signUp.smsInternalError" : "signUp.smsErrorMsg"),
                type: "error",
            });
        }
    }, [sendVerifyCodeState.status, sendVerifyCodeState.data, sendVerifyCodeState.error, isSending, t]);

    // 다날 발송 응답
    useEffect(() => {
        if (portOneSendState.status === "ready" && isSending) {
            setIsSending(false);
            const data = portOneSendState.data?.data;
            if (data?.sent && data?.identityVerificationId) {
                setIdentityVerificationId(data.identityVerificationId);
                setTimeout(() => otpRef.current?.startCountDown(true), 100);
            } else {
                setOtpInfoMsg({ msg: t("signUp.smsErrorMsg"), type: "error" });
            }
            userStore.portOneSend.onDefault();
        } else if (portOneSendState.status === "error" && isSending) {
            setIsSending(false);
            userStore.portOneSend.onDefault();
            // IDENTITY_INFO_MISMATCH — 이름·생년월일·성별·통신사·휴대폰번호가 명의자 정보와
            //   맞지 않는 경우. 사용자가 입력값을 고쳐 재시도할 수 있으므로 확인 항목을 안내한다.
            // 그 외(연동 설정 오류·요청 과다·다날 장애)는 사용자가 고칠 수 없어 기존 문구를 유지한다.
            const errorCode = portOneSendState.error?.code;
            setOtpInfoMsg({
                msg: t(errorCode === "IDENTITY_INFO_MISMATCH"
                    ? "signUp.identityInfoMismatch"
                    : "signUp.smsErrorMsg"),
                type: "error",
            });
        }
    }, [portOneSendState.status, portOneSendState.data, portOneSendState.error, isSending, t]);

    const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value.trim();
        setIsBtnDisabled(v.length !== 6);
        setOtpInfoMsg(undefined);
    };

    const handleVerify = () => {
        // 발송 실패 등으로 인증 세션이 없으면 요청을 보낼 수 없다.
        //   이전에는 isVerifying 을 켠 뒤 return 해서 버튼이 '처리중' 상태로 멈춰 있었다.
        //   요청이 나가지 않으니 응답 처리도 없어 영구히 풀리지 않는다.
        const hasSession = isDanal ? !!identityVerificationId : !!verifyId;
        if (!hasSession) {
            setOtpInfoMsg({ msg: t("signUp.otpSessionMissing"), type: "error" });
            return;
        }

        setIsVerifying(true);
        if (isDanal) {
            userStore.onPortOneConfirm({
                serviceId,
                identityVerificationId,
                otp: otp.trim(),
            });
        } else {
            // DEV 전용: 해외 SMS 인증 시 코드 "000000" 이면 서버 검증 없이 통과시킨다.
            // (config.NODE_ENV !== "prod" 환경에서만 동작 — 운영(prod)에서는 절대 적용되지 않음)
            if (config.NODE_ENV !== "prod" && otp.trim() === "000000") {
                setIsVerifying(false);
                setOtpInfoMsg({ type: "passed", msg: t("signUp.otpPassedMsg") });
                setTimeout(() => {
                    setOtp("");
                    onSuccess({ authType, verifyId: verifyId ?? undefined });
                }, 0);
                return;
            }
            // 위 hasSession 검사로 이미 보장되지만, 타입 좁히기용으로 남긴다.
            // 만약을 대비해 처리중 상태는 반드시 되돌린다.
            if (!verifyId) {
                setIsVerifying(false);
                return;
            }
            userStore.onCheckVerifyCode({
                verifyId,
                verifyCode: otp.trim(),
            });
        }
    };

    // SMS 검증 응답
    useEffect(() => {
        if (checkVerifyCodeState.status === "ready" && isVerifying) {
            setIsVerifying(false);
            const data = checkVerifyCodeState.data?.data;
            if (data?.verified) {
                userStore.checkVerifyCode.onDefault();
                setOtpInfoMsg({ type: "passed", msg: t("signUp.otpPassedMsg") });
                setTimeout(() => {
                    setOtp("");
                    onSuccess({ authType, verifyId: verifyId ?? undefined });
                }, 0);
            } else {
                userStore.checkVerifyCode.onDefault();
                setIsBtnDisabled(true);
                handleOtpError(data?.code);
            }
        } else if (checkVerifyCodeState.status === "error" && isVerifying) {
            setIsVerifying(false);
            userStore.checkVerifyCode.onDefault();
            setOtpInfoMsg({ type: "error", msg: t("signUp.otpErrorMsg") });
            setIsBtnDisabled(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkVerifyCodeState.status, checkVerifyCodeState.data, checkVerifyCodeState.error, isVerifying, verifyId]);

    // 다날 검증 응답
    useEffect(() => {
        if (portOneConfirmState.status === "ready" && isVerifying) {
            setIsVerifying(false);
            const data = portOneConfirmState.data?.data;
            if (data?.verified) {
                userStore.portOneConfirm.onDefault();
                setOtpInfoMsg({ type: "passed", msg: t("signUp.otpPassedMsg") });
                setTimeout(() => {
                    setOtp("");
                    onSuccess({ authType, identityVerificationId: identityVerificationId || "" });
                }, 0);
            } else {
                userStore.portOneConfirm.onDefault();
                setIsBtnDisabled(true);
                setOtpInfoMsg({ type: "error", msg: data?.message || t("signUp.otpErrorMsg") });
            }
        } else if (portOneConfirmState.status === "error" && isVerifying) {
            setIsVerifying(false);
            userStore.portOneConfirm.onDefault();
            setOtpInfoMsg({ type: "error", msg: t("signUp.otpErrorMsg") });
            setIsBtnDisabled(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [portOneConfirmState.status, portOneConfirmState.data, portOneConfirmState.error, isVerifying, identityVerificationId]);

    const handleOtpError = (errorCode?: string) => {
        const msgKey =
            errorCode === "INVALID_CODE" ? "signUp.otpInvalidCode" :
            errorCode === "EXPIRED" ? "signUp.otpExpired" :
            errorCode === "MAX_ATTEMPTS_EXCEEDED" ? "signUp.otpMaxAttempts" :
            errorCode === "NOT_FOUND" ? "signUp.otpNotFound" :
            errorCode === "ALREADY_VERIFIED" ? "signUp.otpAlreadyVerified" :
            "signUp.otpErrorMsg";
        setOtpInfoMsg({ type: "error", msg: t(msgKey) });
    };

    return (
        <div className="identity-otp-form">
            <OTPInput
                ref={otpRef}
                inputVal={otp}
                setInputVal={setOtp}
                time={180}
                resendTime={175}
                onStart={sendAuthRequest}
                onChange={handleOtpChange}
                onClear={() => setIsBtnDisabled(true)}
                infoMsg={otpInfoMsg}
                showClearButton={!isVerifying}
            />

            <div className="identity-bottom-btn">
                <Button
                    text={isSending || isVerifying ? t("signUp.loading") : t("signUp.btnAuthCom")}
                    onPress={handleVerify}
                    disabled={isBtnDisabled || isSending || isVerifying}
                />
            </div>
        </div>
    );
};
